import axios, { AxiosError, isAxiosError } from 'axios';
import { BETTER_AUTH_BASE_PATH } from '@/libs/better-auth-client';
import { toAbsoluteAppUrl } from './auth-utils';
import i18n from '@/libs/i18n/client';

const buildUrl = (path: string) => `${import.meta.env.VITE_API_ROOT}${BETTER_AUTH_BASE_PATH}${path}`;

const AUTH_RECAPTCHA_ENABLED = ['1', 'true', 'yes', 'on'].includes(
    (import.meta.env.VITE_AUTH_RECAPTCHA_ENABLED || '').toLowerCase(),
);

const RECAPTCHA_SCRIPT_ID = 'swonkie-recaptcha-v3-script';

type GrecaptchaClient = {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

declare global {
    interface Window {
        grecaptcha?: GrecaptchaClient;
    }
}

let recaptchaScriptPromise: Promise<void> | null = null;

const createCaptchaClientError = (code: string, message: string) =>
    new AuthApiError(message, {
        code,
    });

const ensureRecaptchaScript = async (siteKey: string) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        throw createCaptchaClientError(
            'CAPTCHA_UNAVAILABLE',
            'Captcha is not available in this environment.',
        );
    }

    if (window.grecaptcha) {
        return;
    }

    if (!recaptchaScriptPromise) {
        recaptchaScriptPromise = new Promise<void>((resolve, reject) => {
            const existing = document.getElementById(RECAPTCHA_SCRIPT_ID);
            if (existing) {
                existing.remove();
            }

            const script = document.createElement('script');
            script.id = RECAPTCHA_SCRIPT_ID;
            script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
            script.async = true;
            script.defer = true;

            script.addEventListener('load', () => resolve(), { once: true });
            script.addEventListener(
                'error',
                () =>
                    reject(
                        createCaptchaClientError(
                            'CAPTCHA_SCRIPT_LOAD_FAILED',
                            'Failed to load reCAPTCHA script.',
                        ),
                    ),
                { once: true },
            );

            document.head.appendChild(script);
        }).catch((error) => {
            recaptchaScriptPromise = null;
            throw error;
        });
    }

    await recaptchaScriptPromise;
};

const executeRecaptcha = async (siteKey: string, action: string) => {
    await ensureRecaptchaScript(siteKey);

    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) {
        throw createCaptchaClientError('CAPTCHA_UNAVAILABLE', 'reCAPTCHA client is unavailable.');
    }

    try {
        return await new Promise<string>((resolve, reject) => {
            grecaptcha.ready(() => {
                grecaptcha
                    .execute(siteKey, { action })
                    .then((token) => {
                        if (typeof token === 'string' && token.trim().length > 0) {
                            resolve(token);
                            return;
                        }

                        reject(
                            createCaptchaClientError(
                                'CAPTCHA_EXECUTION_FAILED',
                                'Failed to generate reCAPTCHA token.',
                            ),
                        );
                    })
                    .catch((error) => reject(error));
            });
        });
    } catch (error) {
        if (error instanceof AuthApiError) {
            throw error;
        }

        throw createCaptchaClientError('CAPTCHA_CLIENT_ERROR', 'Captcha verification failed.');
    }
};

const getCaptchaToken = async (action: string) => {
    if (!AUTH_RECAPTCHA_ENABLED) {
        return null;
    }

    const siteKey = (import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY || '').trim();
    if (!siteKey) {
        throw createCaptchaClientError('CAPTCHA_SITE_KEY_MISSING', 'Captcha site key is not configured.');
    }

    return executeRecaptcha(siteKey, action);
};

const toErrorString = (value: unknown): string | null => {
    if (typeof value === 'string' && value.trim()) {
        return value;
    }

    if (Array.isArray(value)) {
        const stringValues = value.filter(
            (item): item is string => typeof item === 'string' && item.trim().length > 0,
        );
        if (stringValues.length > 0) {
            return stringValues.join(', ');
        }
    }

    return null;
};

const parseErrorPayload = (payload: unknown): { message: string | null; code?: string } => {
    if (!payload || typeof payload !== 'object') {
        return { message: null };
    }

    const withError = payload as {
        message?: unknown;
        code?: unknown;
        error?: unknown;
        error_description?: unknown;
    };

    const nestedError =
        withError.error && typeof withError.error === 'object'
            ? (withError.error as { message?: unknown; code?: unknown })
            : null;

    const message =
        toErrorString(withError.message) ||
        toErrorString(nestedError?.message) ||
        toErrorString(withError.error_description) ||
        toErrorString(withError.error) ||
        null;

    const code =
        toErrorString(withError.code) ||
        toErrorString(nestedError?.code) ||
        toErrorString(withError.error) ||
        undefined;

    return { message, code };
};

export class AuthApiError extends Error {
    readonly code?: string;
    readonly status?: number;

    constructor(message: string, options: { code?: string; status?: number } = {}) {
        super(message);
        this.name = 'AuthApiError';
        this.code = options.code;
        this.status = options.status;
    }
}

export const isAuthApiError = (error: unknown): error is AuthApiError => error instanceof AuthApiError;

const createAuthApiError = (payload: unknown, status: number) => {
    const { message, code } = parseErrorPayload(payload);
    return new AuthApiError(message || `Request failed with status ${status}`, {
        code,
        status,
    });
};

type AuthRequestInit = RequestInit & {
    captchaAction?: string;
};

const request = async <T>(path: string, init: AuthRequestInit = {}) => {
    const { headers: initHeaders, captchaAction, ...requestInit } = init;
    const captchaToken = captchaAction ? await getCaptchaToken(captchaAction) : null;
    const headers: Record<string, string> = {
        'content-type': 'application/json',
        // The UI language (homepage `?language=` or browser language) is what a new account is
        // provisioned with; the backend reads it from Accept-Language, like legacy registration did.
        'accept-language': i18n.language,
    };
    for (const [key, value] of new Headers(initHeaders).entries()) {
        headers[key] = value;
    }
    if (captchaToken) {
        headers['x-captcha-response'] = captchaToken;
    }

    try {
        const response = await axios.request<T>({
            url: buildUrl(path),
            method: requestInit.method,
            data: requestInit.body,
            headers,
            withCredentials: true,
            signal: requestInit.signal ?? undefined,
        });

        return response.data;
    } catch (error) {
        if (isAxiosError(error)) {
            if (error.response) {
                throw createAuthApiError(error.response.data, error.response.status);
            }

            const message = error.message || 'Network request failed';
            throw new AuthApiError(message, { code: 'NETWORK_ERROR' });
        }

        const message = error instanceof Error && error.message ? error.message : 'Network request failed';
        throw new AuthApiError(message, { code: 'NETWORK_ERROR' });
    }
};

type AuthApi = {
    getSession: () => Promise<{ session: unknown; user: unknown } | null>;
    signInEmail: (input: {
        email: string;
        password: string;
        callbackURL?: string;
        oauthQuery?: string;
    }) => Promise<{
        redirect?: boolean;
        url?: string;
        token?: string;
        twoFactorRedirect?: boolean;
        twoFactorMethods?: string[];
        user?: Record<string, unknown>;
    }>;
    verifyTotp: (input: { code: string; trustDevice?: boolean }) => Promise<{ token: string; user: unknown }>;
    sendOtp: (input?: { trustDevice?: boolean }) => Promise<{ status: boolean }>;
    verifyOtp: (input: { code: string; trustDevice?: boolean }) => Promise<{ token: string; user: unknown }>;
    signUpEmail: (input: {
        name: string;
        email: string;
        password: string;
        phoneNumber?: string;
        callbackURL?: string;
        oauthQuery?: string;
    }) => Promise<{ token: string | null }>;
    signInMagicLink: (input: {
        email: string;
        callbackURL?: string;
        errorCallbackURL?: string;
        oauthQuery?: string;
    }) => Promise<{ status: boolean }>;
    listAccounts: () => Promise<
        Array<{
            id: string;
            providerId: string;
            accountId: string;
        }>
    >;
    getInvitation: (invitationId: string) => Promise<{
        id: string;
        role: string;
        email: string;
        organizationName: string;
        organizationSlug: string;
    }>;
    acceptInvitation: (invitationId: string) => Promise<{ invitation: { id: string } }>;
    rejectInvitation: (invitationId: string) => Promise<{ invitation: { id: string } }>;
    setActiveOrganization: (input: {
        organizationId?: string;
        organizationSlug?: string;
    }) => Promise<unknown>;
    forgotPassword: (input: { email: string; redirectTo?: string }) => Promise<{ status: boolean }>;
    resetPassword: (input: { newPassword: string; token: string }) => Promise<{ status: boolean }>;
    verifyLegacyTotp: (input: { code: string }) => Promise<{ valid: boolean }>;
    completeTwoFactorReenrollment: () => Promise<{ success: boolean }>;
    getTwoFactorStatus: () => Promise<{
        enabled: boolean;
        hasTotp: boolean;
        requiresReenrollment: boolean;
    }>;
    getSecuritySessionStatus: () => Promise<{
        elevated: boolean;
        expiresAt: number | null;
        /** False for a legacy-only session: the request carries no Better Auth session at all. */
        hasSession: boolean;
    }>;
    verifyPasswordSecuritySession: (input: {
        password: string;
    }) => Promise<{ elevated: boolean; expiresAt: number | null }>;
    setPassword: (input: { newPassword: string }) => Promise<{ status: boolean }>;
    startSocialReauth: () => Promise<{ ok: boolean }>;
    sendSecuritySessionOtp: () => Promise<{ status: boolean }>;
    verifySecuritySessionOtp: (input: {
        code: string;
    }) => Promise<{ elevated: boolean; expiresAt: number | null }>;
};

export const authApi: AuthApi = {
    getSession: () => request<{ session: unknown; user: unknown } | null>('/get-session', { method: 'GET' }),
    signInEmail: (input) =>
        request<{
            redirect?: boolean;
            url?: string;
            token?: string;
            twoFactorRedirect?: boolean;
            twoFactorMethods?: string[];
            user?: Record<string, unknown>;
        }>('/sign-in/email', {
            method: 'POST',
            captchaAction: 'sign_in_email',
            body: JSON.stringify({
                email: input.email,
                password: input.password,
                callbackURL: input.callbackURL,
                ...(input.oauthQuery ? { oauth_query: input.oauthQuery } : {}),
            }),
        }),
    verifyTotp: (input) =>
        request<{ token: string; user: unknown }>('/two-factor/verify-totp', {
            method: 'POST',
            body: JSON.stringify(input),
        }),
    sendOtp: (input) =>
        request<{ status: boolean }>('/two-factor/send-otp', {
            method: 'POST',
            body: JSON.stringify(input ?? {}),
        }),
    verifyOtp: (input) =>
        request<{ token: string; user: unknown }>('/two-factor/verify-otp', {
            method: 'POST',
            body: JSON.stringify(input),
        }),
    signUpEmail: (input) =>
        request<{ token: string | null }>('/sign-up/email', {
            method: 'POST',
            captchaAction: 'sign_up_email',
            body: JSON.stringify({
                name: input.name,
                email: input.email,
                password: input.password,
                phoneNumber: input.phoneNumber,
                callbackURL: input.callbackURL,
                ...(input.oauthQuery ? { oauth_query: input.oauthQuery } : {}),
            }),
        }),
    signInMagicLink: (input) =>
        request<{ status: boolean }>('/sign-in/magic-link', {
            method: 'POST',
            captchaAction: 'sign_in_magic_link',
            body: JSON.stringify({
                email: input.email,
                callbackURL: input.callbackURL,
                errorCallbackURL: input.errorCallbackURL,
                ...(input.oauthQuery ? { oauth_query: input.oauthQuery } : {}),
            }),
        }),
    listAccounts: () =>
        request<
            Array<{
                id: string;
                providerId: string;
                accountId: string;
            }>
        >('/list-accounts', { method: 'GET' }),
    getInvitation: (invitationId) =>
        request<{
            id: string;
            role: string;
            email: string;
            organizationName: string;
            organizationSlug: string;
        }>(`/organization/get-invitation?id=${encodeURIComponent(invitationId)}`, { method: 'GET' }),
    acceptInvitation: (invitationId) =>
        request<{ invitation: { id: string } }>('/organization/accept-invitation', {
            method: 'POST',
            body: JSON.stringify({ invitationId }),
        }),
    rejectInvitation: (invitationId) =>
        request<{ invitation: { id: string } }>('/organization/reject-invitation', {
            method: 'POST',
            body: JSON.stringify({ invitationId }),
        }),
    setActiveOrganization: (input) =>
        request<unknown>('/organization/set-active', {
            method: 'POST',
            body: JSON.stringify(input),
        }),
    forgotPassword: (input) =>
        request<{ status: boolean }>('/request-password-reset', {
            method: 'POST',
            captchaAction: 'request_password_reset',
            body: JSON.stringify(input),
        }),
    resetPassword: (input) =>
        request<{ status: boolean }>('/reset-password', {
            method: 'POST',
            body: JSON.stringify(input),
        }),
    verifyLegacyTotp: (input) =>
        request<{ valid: boolean }>('/two-factor/verify-legacy-totp', {
            method: 'POST',
            body: JSON.stringify(input),
        }),
    completeTwoFactorReenrollment: () =>
        request<{ success: boolean }>('/two-factor/complete-reenrollment', {
            method: 'POST',
        }),
    getTwoFactorStatus: () =>
        request<{ enabled: boolean; hasTotp: boolean; requiresReenrollment: boolean }>(
            '/two-factor/status',
            { method: 'GET' },
        ),
    getSecuritySessionStatus: () =>
        request<{ elevated: boolean; expiresAt: number | null; hasSession: boolean }>(
            '/security-session/status',
            { method: 'GET' },
        ),
    verifyPasswordSecuritySession: (input) =>
        request<{ elevated: boolean; expiresAt: number | null }>('/security-session/verify-password', {
            method: 'POST',
            body: JSON.stringify(input),
        }),
    setPassword: (input) =>
        request<{ status: boolean }>('/security-session/set-password', {
            method: 'POST',
            body: JSON.stringify(input),
        }),
    startSocialReauth: () =>
        request<{ ok: boolean }>('/security-session/start-social-reauth', { method: 'POST' }),
    sendSecuritySessionOtp: () =>
        request<{ status: boolean }>('/security-session/send-otp', { method: 'POST' }),
    verifySecuritySessionOtp: (input) =>
        request<{ elevated: boolean; expiresAt: number | null }>('/security-session/verify-otp', {
            method: 'POST',
            body: JSON.stringify(input),
        }),
};

const extractErrorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof AxiosError) {
        const message = error.response?.data?.message;
        if (typeof message === 'string' && message.length > 0) return message;
    }
    return fallback;
};

export const startSocialSignIn = async (
    provider: string,
    callbackURL: string,
    errorCallbackURL: string,
    oauthQuery: string | undefined,
    onError: (message: string) => void,
): Promise<void> => {
    try {
        const response = await axios.post<{ url: string }>(
            buildUrl('/sign-in/social'),
            {
                provider,
                // Absolute frontend URLs — Better Auth resolves a relative callbackURL against the
                // backend origin, which would land the user on the API instead of the web app.
                callbackURL: toAbsoluteAppUrl(callbackURL),
                errorCallbackURL: toAbsoluteAppUrl(errorCallbackURL),
                ...(oauthQuery ? { oauth_query: oauthQuery } : {}),
            },
            { withCredentials: true },
        );
        window.location.href = response.data.url;
    } catch (error) {
        onError(extractErrorMessage(error, 'Social sign-in failed. Please try again.'));
    }
};

export const startLinkSocial = async (
    provider: string,
    callbackURL: string,
    errorCallbackURL: string,
    onError: (message: string) => void,
): Promise<void> => {
    try {
        const response = await axios.post<{ url: string }>(
            buildUrl('/link-social'),
            {
                provider,
                callbackURL: toAbsoluteAppUrl(callbackURL),
                errorCallbackURL: toAbsoluteAppUrl(errorCallbackURL),
            },
            { withCredentials: true },
        );
        // Navigate in the SAME window (not a popup): on failure Better Auth redirects back to
        // errorCallbackURL with `?error=<code>`, which the settings page reads to show the specific
        // cause. A popup would dead-end that error in a detached window.
        window.location.href = response.data.url;
    } catch (error) {
        onError(extractErrorMessage(error, 'Social account linking failed. Please try again.'));
    }
};
